import {DataStatus, Flag, Label, Parameter, Parameters, Rank, Tooltip} from 'platform/components';
import {Box, getSize, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {ReactElement} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {
  buildArray,
  EMPTY_PLACEHOLDER,
  isNotNilOrEmpty,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useVehicleDoubleClick} from '../../hooks/useVehicleDoubleClick';
import {CarStatusFilter} from '../../pages/AllOffers/types/CarStatusFilter';
import {AuctionSourcingListItem} from '../../utils/adaptAuctionVehicleToSourcingItem';
import {buildAuctionVehicleTitle} from '../../utils/buildAuctionVehicleTitle';
import {getAuctionDisplayPrice} from '../../utils/getAuctionDisplayPrice';
import {getAuctionTypePresentation} from '../../utils/getAuctionTypePresentation';
import {getSealedAuctionPrices} from '../../utils/getSealedAuctionPrices';
import {getSuspiciousReasonsLabel} from '../../utils/getSuspiciousReasonsLabel';
import {isHiddenAuctionType} from '../../utils/isHiddenAuctionType';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {resolveAuctionEnumLabel} from '../../utils/resolveAuctionEnumLabel';
import {ActionButtonsVehicle} from '../ActionButtonsVehicle/ActionButtonsVehicle';
import {AuctionCountdownCell} from '../AuctionPreview/components/AuctionCountdownCell';
import {Clamp} from '../Clamp/Clamp';
import {CountryFlag} from '../CountryFlag/CountryFlag';
import {HitlistBlacklistFlag} from '../HitlistBlacklistFlag/HitlistBlacklistFlag';
import {Car, CarContentColumn, CarImageColumn} from '../SourcingCard/Car';
import {CardStatsGrid} from '../SourcingCard/CardStatsGrid';
import {SOURCING_CARD_GEOMETRY} from '../SourcingCard/constants/SOURCING_CARD_GEOMETRY';
import {createRowElementId} from '../SourcingTable/utils/createRowElementId';
import {convertVehicleTagsToVehicleHighlights} from '../VehicleHighlights/utils/convertVehicleTagsToVehicleHighlights';
import {getInteriorMaterialHighlights} from '../VehicleHighlights/utils/getInteriorMaterialHighlights';
import {VehicleHighlights} from '../VehicleHighlights/VehicleHighlights';
import {VehicleImage} from '../VehicleImage/VehicleImage';

export interface AdvanceAuctionViewProps extends TestIdProps {
  index?: number;
  isDetail?: boolean;
  vehicle?: AuctionSourcingListItem;
  isVehicleLoading?: boolean;
  onRowClick?: () => void;
  isLastClickedRow?: boolean;
  setLastVisitedVehicle?: (adId: string, index: number) => void;
  children?: ReactElement;
}

const PERCENTAGE_MULTIPLIER = 100;

// eslint-disable-next-line complexity -- Pre-existing violation
export function AdvanceAuctionView(props: AdvanceAuctionViewProps) {
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const onDoubleClick = useVehicleDoubleClick(props.vehicle?.sourcingVehicle.adId, () =>
    props.setLastVisitedVehicle?.(props.vehicle?.sourcingVehicle.adId ?? '', props.index ?? 0)
  );
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const enumLookup = useAuctionEnumLookup();
  const sourcingVehicle =
    props.vehicle && 'sourcingVehicle' in props.vehicle ? props.vehicle.sourcingVehicle : null;
  const interiorMaterialHighlights = getInteriorMaterialHighlights(
    sourcingVehicle?.interiorMaterial
  );
  const auctionVehicle = props.vehicle?.auctionVehicle;
  const suspiciousReasons = getSuspiciousReasonsLabel(sourcingVehicle?.suspiciousReason);
  const firstLine = buildAuctionVehicleTitle(
    auctionVehicle?.make ?? null,
    auctionVehicle?.model ?? null,
    auctionVehicle?.adTitle ?? null,
    enumLookup
  );
  const auctionTypePresentation = getAuctionTypePresentation(auctionVehicle?.auctionType);
  const secondLineParams = buildArray<Parameter>()
    .when(isNotNil(auctionTypePresentation), {
      icon: auctionTypePresentation?.icon,
      tooltip: auctionTypePresentation?.label,
    })
    .when(isNotNilOrEmpty(auctionVehicle?.year), String(auctionVehicle?.year ?? ''))
    .when((auctionVehicle?.power ?? 0) > 0, `${auctionVehicle?.power} kW`)
    .when(
      isNotNilOrEmpty(auctionVehicle?.transmission),
      resolveAuctionEnumLabel(auctionVehicle?.transmission ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.driveType),
      resolveAuctionEnumLabel(auctionVehicle?.driveType ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.fuelType),
      resolveAuctionEnumLabel(auctionVehicle?.fuelType ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.carStyle),
      resolveAuctionEnumLabel(auctionVehicle?.carStyle ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.mileage),
      `${formatNumber(auctionVehicle?.mileage ?? 0, 0)} km`
    );

  const marketPosition = auctionVehicle?.marketPosition;
  const marketPositionCount = auctionVehicle?.marketPositionCount;
  const auctionCurrency = auctionVehicle?.currency;
  const isHiddenType = isHiddenAuctionType(auctionVehicle?.auctionType);
  const displayPrice = auctionVehicle ? getAuctionDisplayPrice(auctionVehicle) : null;
  const sealedPrices = auctionVehicle ? getSealedAuctionPrices(auctionVehicle) : [];
  const formattedCurrentBid =
    (displayPrice ?? 0) > 0
      ? (formatCurrency(displayPrice ?? 0, auctionCurrency ?? '', 0) ?? EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;
  const formattedMargin =
    !isHiddenType && isNotNil(auctionVehicle?.maximalPossibleMargin)
      ? (formatCurrency(auctionVehicle?.maximalPossibleMargin ?? 0, auctionCurrency ?? '', 0) ??
        EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;
  const formattedRecommendedPrice = isNotNil(auctionVehicle?.predictedPrice)
    ? (formatCurrency(auctionVehicle?.predictedPrice ?? 0, auctionCurrency ?? '', 0) ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;
  const positionText =
    isNotNil(marketPosition) && isNotNil(marketPositionCount)
      ? `${marketPosition}/${marketPositionCount}`
      : EMPTY_PLACEHOLDER;
  const positionPercentage =
    isNotNil(marketPosition) && isNotNil(marketPositionCount) && marketPositionCount > 0
      ? Math.round(((marketPositionCount - marketPosition) / marketPositionCount) * 100)
      : null;
  const percentText = isNotNil(positionPercentage) ? `${positionPercentage}%` : undefined;

  const getActionButtons = () => {
    if (isNil(props.vehicle)) {
      return null;
    }

    return (
      <ActionButtonsVehicle
        vehicle={props.vehicle}
        onDetailOpen={() =>
          props.setLastVisitedVehicle?.(auctionVehicle?.adId ?? '', props.index ?? 0)
        }
        data-testid={suffixTestId('actionButtonsVehicle', props)}
      />
    );
  };

  const actionButtons = getActionButtons();

  return (
    <Car
      $enhancedSurfaces={enhancedSurfaces}
      id={props.vehicle && createRowElementId(props.vehicle)}
      key={sourcingVehicle?.adId}
      $isDetail={props.isDetail}
      $isActive={props.isLastClickedRow}
      $isNew={props.vehicle?.isNew}
      onClick={props.onRowClick}
      onDoubleClick={props.isDetail ? undefined : onDoubleClick}
    >
      <CarImageColumn>
        <VehicleImage
          vehicle={props.vehicle}
          borderRadius="medium"
          hasUnifiedSurfaces={enhancedSurfaces}
        />
      </CarImageColumn>
      <CarContentColumn>
        <DataStatus isLoading={props.isVehicleLoading}>
          <Box padding={3}>
            <VStack spacing={enhancedSurfaces ? 3 : 2}>
              <HStack align="center" spacing={2} wrap justify="flex-end">
                {/* The flags and the unread dot must stay inside this group: as siblings of the row
                    they get wrapped away from the title. minWidth={0} lets the title's Clamp still
                    ellipsize; when the group cannot get its basis, the action buttons wrap below it. */}
                <HStack
                  align="center"
                  spacing={2}
                  grow={1}
                  minWidth={0}
                  basis={getSize(SOURCING_CARD_GEOMETRY.titleMinWidth)}
                >
                  <CountryFlag country={sourcingVehicle?.seller?.country?.key} />
                  <Show when={isTrue(sourcingVehicle?.isSold)}>
                    <Flag
                      label={i18n.t('entity.vehicle.labels.sold')}
                      size="small"
                      data-testid={suffixTestId('sold', props)}
                    />
                  </Show>
                  <Show when={props.vehicle?.isNew}>
                    <Box
                      backgroundColor="palettes.green.60.100"
                      width={3}
                      height={3}
                      borderRadius="circular"
                    />
                  </Show>
                  <Tooltip label={firstLine}>
                    <Clamp lines={1}>
                      <Heading size={4} data-testid={suffixTestId('vehicleName', props)}>
                        {firstLine}
                      </Heading>
                    </Clamp>
                  </Tooltip>
                  <Show when={props.vehicle?.vehicleSummary?.comment}>
                    <Tooltip placement="top" label={props.vehicle?.vehicleSummary?.comment}>
                      <Icon value="communication/comment" />
                    </Tooltip>
                  </Show>
                  <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                    <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
                  </Show>
                  <Show
                    whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                    when={sourcingVehicle?.suspicious}
                  >
                    <Tooltip label={suspiciousReasons}>
                      <Icon value="alert/warning" color="severity.warning" size={5} />
                    </Tooltip>
                  </Show>
                </HStack>
                <Box flexShrink={0}>{actionButtons}</Box>
              </HStack>
              <Show when={isNotNil(sourcingVehicle)}>
                <Parameters
                  size="small"
                  color="secondary"
                  parameters={secondLineParams}
                  data-testid={suffixTestId('vehicleParameters', props)}
                />
              </Show>
              <VehicleHighlights
                data={convertVehicleTagsToVehicleHighlights(
                  [],
                  [],
                  Object.values(props.vehicle?.features ?? {}).filter((item) => item.isHighlight),
                  interiorMaterialHighlights
                )}
              />
              <CardStatsGrid $enhancedSurfaces={enhancedSurfaces}>
                <VStack align="flex-start">
                  <Show when={isHiddenType && sealedPrices.length > 0}>
                    {sealedPrices.map((price) => (
                      <VStack key={price.kind} spacing={1} align="flex-start">
                        <Label>{i18n.t(price.labelKey)}</Label>
                        <Text
                          size={enhancedSurfaces ? 'small' : 'xSmall'}
                          alternative={enhancedSurfaces}
                        >
                          {formatCurrency(price.price, auctionCurrency ?? '', 0) ??
                            EMPTY_PLACEHOLDER}
                        </Text>
                      </VStack>
                    ))}
                  </Show>
                  <Show when={!isHiddenType || sealedPrices.length === 0}>
                    <Label>{i18n.t('general.labels.price')}</Label>
                    <Text
                      size={enhancedSurfaces ? 'small' : 'xSmall'}
                      alternative={enhancedSurfaces}
                    >
                      {isHiddenType
                        ? i18n.t('entity.auction.labels.priceNotDisclosed')
                        : formattedCurrentBid}
                    </Text>
                  </Show>
                </VStack>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.margin')}</Label>
                  <Text
                    size="xSmall"
                    color={
                      !isHiddenType &&
                      isNotNil(auctionVehicle?.maximalPossibleMargin) &&
                      auctionVehicle.maximalPossibleMargin < 0
                        ? 'danger'
                        : undefined
                    }
                  >
                    {formattedMargin}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.recommendedPrice')}</Label>
                  <Text size="xSmall" alternative={enhancedSurfaces}>
                    {formattedRecommendedPrice}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.position')}</Label>
                  <Text size="xSmall">{positionText}</Text>
                  <Show when={isNotNil(percentText)}>
                    <Space vertical={1} />
                    <Text size="xxSmall" color="tertiary">
                      {percentText}
                    </Text>
                  </Show>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.LID')}</Label>
                  <Text size="xSmall">
                    {isNotNil(auctionVehicle?.turnover)
                      ? formatNumber(auctionVehicle.turnover, 0)
                      : EMPTY_PLACEHOLDER}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.features')}</Label>
                  <Rank
                    percentage={Math.floor(
                      (auctionVehicle?.featureScore ?? 0) * PERCENTAGE_MULTIPLIER
                    )}
                    data-testid={suffixTestId('rank', props)}
                  />
                </Box>
                <Box>
                  <Label>{i18n.t('entity.auction.labels.auctionEnd')}</Label>
                  <Text size="xSmall">
                    <AuctionCountdownCell
                      endTime={auctionVehicle?.carEndTime}
                      isEnded={auctionVehicle?.carStatus === CarStatusFilter.ENDED}
                    />
                  </Text>
                </Box>
              </CardStatsGrid>
            </VStack>
          </Box>
        </DataStatus>
      </CarContentColumn>
    </Car>
  );
}
